/* eslint-disable prefer-rest-params */
import styled from '@emotion/styled';
import {
  AvatarAndName,
  Breadcrumbs,
  EnabledDisabledBadge,
  FCOIconAndName,
  FCOTagBadge,
  FlexGroup,
  FlexGroupWrapper,
  FlexItem,
  Monospace,
  Text,
  TruncateText,
} from '@tecton/ComponentRedesign';
import React, { FC } from 'react';
// @svg
import { Link } from 'react-router-dom';
import { FCOTypeHumanReadable, fcoTypeRecord, FCOType } from '../../core/types/fcoTypes';
import { FcoType } from '../../types/tecton_proto/metadataservice/metadata_service';
import { getFcoData } from './GlobalSearch';
import { GlobalSearchItemProps } from './GlobalSearchTypes';
import WorkspaceRootBreadcrumb from './WorkspaceRootBreadcrumb';
import { renderDependencyList } from './compute/ComputeEnvironmentsContainer';

export const breakPointForHidingFCODetails = '1008px';

const DetailWrapper = styled.div`
  line-height: ${({ theme }) => theme.font.lineHeights.s};
  padding: ${({ theme }) => theme.padding.xl};
`;

const FcoNameStyle = styled.div`
  .tooltipName {
    color: ${({ theme }) => theme.colors.title};
    font-size: ${({ theme }) => theme.font.fontSizes.m};
  }

  @media (max-width: calc(${breakPointForHidingFCODetails} + 64px)) {
    div {
      max-width: 248px;
    }
  }
`;

const DescriptionWrapper = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightShade};
  padding-bottom: ${({ theme }) => theme.gap.m};
`;

const IdWrapper = styled.div`
  max-width: 21vw;
`;

const FCOAttributeValueStyle = styled.div`
  color: ${({ theme }) => theme.colors.title};
`;

const DetailFlexGroupWrapper = styled(FlexGroup)`
  flex-direction: row;
  width: 100%;
  @media only screen and (max-width: ${breakPointForHidingFCODetails}) {
    flex-direction: column;
    width: unset;
  }
`;

const FlexFieldNameWrapper = styled(FlexItem)`
  flex-basis: 80px; // Force this column to be a specific width so we get more space for the values
  flex-grow: 0;
  flex-shrink: 0;

  @media only screen and (max-width: calc(${breakPointForHidingFCODetails})) {
    flex-direction: column;
    flex-basis: 0px; // Force this column to be a specific width so we get more space for the values
  }
`;

const SearchResultSelected: FC<{
  searchResult: GlobalSearchItemProps;
  setShowSearchModal: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ searchResult, setShowSearchModal }) => {
  const isEnv = searchResult.type === 'ENVIRONMENT';
  const owner = searchResult?.owner ? (
    <AvatarAndName name={searchResult?.owner ?? ''} />
  ) : (
    <AvatarAndName icon={'NotSet'} name="Not Set" type="icon" />
  );

  const fcoType = isEnv ? 'ENVIRONMENT' : fcoTypeRecord[searchResult.type as FcoType];
  const readableFCO = isEnv ? 'Environment' : (fcoType && FCOTypeHumanReadable[fcoType as FCOType]) || '';
  const url = isEnv
    ? `data-platform/environments?id=${searchResult.id}`
    : `repo/${searchResult?.workspace?.name}/${getFcoData(searchResult.type).typeUrl}/`;
  const fcoUrl = isEnv ? url : `${url}/${searchResult.name}`;

  const FCOAttribute: FC<{ value: React.ReactNode; title: string }> = ({ title, value }) => {
    return (
      <>
        <DetailFlexGroupWrapper gutterSize="m" responsive={true} alignItems="flexStart" justifyContent="flexStart">
          <FlexFieldNameWrapper grow={false}>
            <Text weight="regular" small={true}>
              {title}
            </Text>
          </FlexFieldNameWrapper>
          <FlexItem grow={true}>
            <FCOAttributeValueStyle>{value}</FCOAttributeValueStyle>
          </FlexItem>
        </DetailFlexGroupWrapper>
      </>
    );
  };

  const workspace = (
    <>
      <WorkspaceRootBreadcrumb
        workspace={searchResult?.workspace}
        variant="small"
        maxWidth="180px"
        onClick={() => {
          setShowSearchModal(false);
        }}
      />
    </>
  );

  return (
    <DetailWrapper>
      <FlexGroupWrapper direction="column" gap="l">
        <FlexItem grow={false}>
          <FlexGroupWrapper direction="column" gap="m">
            <FlexItem>
              {isEnv ? (
                <>Compute </>
              ) : (
                <Breadcrumbs
                  variant="small"
                  workspace={workspace}
                  crumbs={[{ label: readableFCO, url }]}
                  onClick={() => {
                    setShowSearchModal(false);
                  }}
                />
              )}
            </FlexItem>
            <FlexItem grow={false}>
              <FcoNameStyle>
                <Link
                  to={fcoUrl}
                  onClick={() => {
                    setShowSearchModal(false);
                  }}
                >
                  <FCOIconAndName
                    name={searchResult?.name ?? ''}
                    type={isEnv ? 'ENVIRONMENT' : (fcoType as FCOType | 'ENVIRONMENT')}
                    isModified={false}
                    maxWidthName="294px"
                  />
                </Link>
              </FcoNameStyle>
            </FlexItem>
            <FlexItem grow={false}>
              <DescriptionWrapper>{searchResult?.description ?? ''}</DescriptionWrapper>
            </FlexItem>
          </FlexGroupWrapper>
        </FlexItem>
        <FlexItem grow={false}>
          <FlexGroupWrapper
            direction="column"
            gap="m"
            responsive={true}
            alignItems="flexStart"
            justifyContent="flexStart"
          >
            <FCOAttribute
              value={
                <Text weight="regular" element={'h6'}>
                  {searchResult.lastUpdated ?? ''}
                </Text>
              }
              title="Last Updated"
            />
            {!isEnv && <FCOAttribute title="Owner" value={owner} />}

            <FCOAttribute
              value={
                <IdWrapper>
                  <Text element={'h6'}>
                    <Monospace>
                      <TruncateText>{searchResult.id ?? ''}</TruncateText>
                    </Monospace>
                  </Text>
                </IdWrapper>
              }
              title="ID"
            />

            {searchResult.type === FcoType.FCO_TYPE_FEATURE_VIEW && (
              <>
                <FCOAttribute
                  title="Offline"
                  value={<EnabledDisabledBadge enabled={!!searchResult.materializationOffline} />}
                />
              </>
            )}

            {searchResult.type === FcoType.FCO_TYPE_FEATURE_VIEW && (
              <>
                <FCOAttribute
                  title="Online"
                  value={<EnabledDisabledBadge enabled={!!searchResult.materializationOnline} />}
                />
              </>
            )}
            {/* ENVIRONMENTS DON"T SHOW TAGS */}
            {searchResult.type !== 'ENVIRONMENT' && (
              <FCOAttribute
                value={
                  <>
                    {(searchResult.tags ?? []).map((tag, i) => {
                      return (
                        <div key={i}>
                          <FCOTagBadge tag={tag} maxWidth={'190px'} />
                        </div>
                      );
                    })}
                  </>
                }
                title="Tags"
              />
            )}
            {/* ENVIRONMENTS SHOW PIP DEPS */}
            {searchResult.type === 'ENVIRONMENT' && (
              <FCOAttribute value={renderDependencyList(searchResult?.dependencies)} title="Dependencies" />
            )}
          </FlexGroupWrapper>
        </FlexItem>
      </FlexGroupWrapper>
    </DetailWrapper>
  );
};

export default SearchResultSelected;
